import React, { useEffect, useRef } from "react";
import Print from "@mui/icons-material/Print";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import VideocamRounded from "@mui/icons-material/VideocamRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import CheckRounded from "@mui/icons-material/CheckRounded";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Footer from "../../interviewAI/layout/Footer";
import logo from "../assets/executive-report-logo.png";
import reportAvatar from "../assets/executive-report-avatar.png";
import "./ExecutiveInterviewReport.css";

const numeric = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value));
const percent = (value, max = 100) =>
  numeric(value) && numeric(max) && Number(max) > 0
    ? Math.min(100, Math.max(0, (Number(value) / Number(max)) * 100))
    : 0;
const scoreText = (value) => (numeric(value) ? Number(value).toFixed(1) : "—");
const weight = (value) => (numeric(value) ? `${value}%` : value);
const scoreClass = (qa) =>
  !numeric(qa.score)
    ? "na"
    : percent(qa.score, qa.marks || 10) >= 95
      ? "s10"
      : percent(qa.score, qa.marks || 10) >= 85
        ? "s9"
        : "s8";

function AnimatedReportNetwork() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return undefined;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const pointer = { x: -999, y: -999 };
    let frame;
    let width = 0;
    let height = 0;
    let points = [];

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = bounds.width;
      height = bounds.height;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      points = Array.from(
        { length: Math.round(Math.min(70, (width * height) / 18000)) },
        () => ({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.25,
          vy: (Math.random() - 0.5) * 0.25,
          r: Math.random() * 2 + 1.2,
        }),
      );
    };

    const draw = () => {
      context.clearRect(0, 0, width, height);
      points.forEach((point, index) => {
        if (!reducedMotion) {
          point.x += point.vx;
          point.y += point.vy;
          if (point.x < 0 || point.x > width) point.vx *= -1;
          if (point.y < 0 || point.y > height) point.vy *= -1;
        }
        const fade = Math.min(
          1,
          Math.max(
            0.12,
            (Math.abs(point.x - width / 2) / (width / 2 || 1)) * 1.4,
          ),
        );
        points.slice(index + 1).forEach((other) => {
          const distance = Math.hypot(point.x - other.x, point.y - other.y);
          if (distance < 150) {
            context.strokeStyle = `rgba(220,200,255,${(1 - distance / 150) * 0.34 * fade})`;
            context.lineWidth = 1;
            context.beginPath();
            context.moveTo(point.x, point.y);
            context.lineTo(other.x, other.y);
            context.stroke();
          }
        });
        const mouseDistance = Math.hypot(
          point.x - pointer.x,
          point.y - pointer.y,
        );
        if (mouseDistance < 180) {
          context.strokeStyle = `rgba(255,255,255,${(1 - mouseDistance / 180) * 0.28})`;
          context.beginPath();
          context.moveTo(point.x, point.y);
          context.lineTo(pointer.x, pointer.y);
          context.stroke();
        }
        context.fillStyle = `rgba(${index % 3 ? "220,200,255" : "255,255,255"},${0.72 * fade})`;
        context.beginPath();
        context.arc(point.x, point.y, point.r, 0, Math.PI * 2);
        context.fill();
      });
      if (!reducedMotion) frame = window.requestAnimationFrame(draw);
    };

    const onPointerMove = (event) => {
      const bounds = canvas.getBoundingClientRect();
      pointer.x = event.clientX - bounds.left;
      pointer.y = event.clientY - bounds.top;
    };
    const onPointerLeave = () => {
      pointer.x = -999;
      pointer.y = -999;
    };

    resize();
    draw();
    const hero = canvas.parentElement?.parentElement;
    hero?.addEventListener("pointermove", onPointerMove);
    hero?.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("resize", resize);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      hero?.removeEventListener("pointermove", onPointerMove);
      hero?.removeEventListener("pointerleave", onPointerLeave);
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden="true" />;
}

function EmptyReport({ title, message }) {
  return (
    <div className="card report-empty">
      <h3>{title}</h3>
      <p>{message}</p>
    </div>
  );
}

function Skill({ skill }) {
  return (
    <div className="skill">
      <div className="skill-top">
        <span className="skill-name">
          {skill.display_name || skill.tech || "Skill"}
        </span>
        {skill.complexity && <span className="tag">{skill.complexity}</span>}
        {skill.weightage !== null && skill.weightage !== undefined && (
          <span className="tag w">Weightage: {weight(skill.weightage)}</span>
        )}
        <span className="skill-score">
          {skill.score ?? "—"}
          <small>/{skill.max || 10}</small>
        </span>
      </div>
      <div
        className="meter"
        role="progressbar"
        aria-label={`${skill.display_name || skill.tech} score`}
        aria-valuenow={percent(skill.score, skill.max || 10)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <i style={{ width: `${percent(skill.score, skill.max || 10)}%` }} />
      </div>
    </div>
  );
}

function Recording({
  url,
  secondary,
  videoRef,
  flagged,
  events,
  duration,
  jumpToTimestamp,
  activeEventIndex,
  parseTimestampToSeconds,
}) {
  const label = secondary ? "Secondary camera" : "Main camera";
  const formatDuration = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "—";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
  };
  const timelineMarkers = events
    .map((event, index) => ({
      event,
      index,
      seconds: parseTimestampToSeconds(event.timestamp),
    }))
    .filter((item) => Number.isFinite(item.seconds))
    .sort((first, second) => first.seconds - second.seconds)
    .reduce((groups, item) => {
      const previousGroup = groups[groups.length - 1];
      // Nearby events share one marker. This keeps dense sessions readable
      // while every individual event remains available in the event list.
      const minimumGap = Math.max((duration || 0) * 0.022, 8);
      if (previousGroup && item.seconds - previousGroup.seconds < minimumGap) {
        previousGroup.items.push(item);
        return groups;
      }
      groups.push({ seconds: item.seconds, items: [item] });
      return groups;
    }, []);
  return (
    <div className={`card ${flagged ? "player" : "rec"}`}>
      {flagged ? (
        <div className="player-head">
          <VideocamRounded />
          <b>{label}</b>
          <span className="badge-dark">
            {secondary ? "Secondary" : "Primary"}
          </span>
        </div>
      ) : (
        <>
          <div className="eyebrow">Recording Links</div>
          <div className="rec-top">
            <a
              className="play"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${label}`}
            >
              <PlayArrowRounded />
            </a>
            <div>
              <h3>{label}</h3>
              <p>Full-length session video · opens in a new tab</p>
            </div>
          </div>
        </>
      )}
      {flagged ? (
        <video
          className="report-video no-print"
          ref={videoRef}
          src={url}
          controls
          playsInline
          preload="metadata"
          aria-label={label}
        />
      ) : (
        <details className="inline-playback no-print">
          <summary>Play recording in this report</summary>
          <video
            className="report-video"
            ref={videoRef}
            src={url}
            controls
            playsInline
            preload="metadata"
            aria-label={label}
          />
        </details>
      )}
      {flagged && !secondary && events.length > 0 && duration > 0 && (
        <div className="scrub no-print">
          <div className="scrub-top">
            <span>0:00</span>
            <span>{formatDuration(duration)}</span>
          </div>
          <div className="track">
            <div className="rail" />
            {timelineMarkers.map((marker) => {
              const representative = marker.items[0];
              const count = marker.items.length;
              return (
                <button
                  type="button"
                  key={representative.index}
                  className={`mk ${count > 1 ? "cluster" : ""} ${activeEventIndex === representative.index ? "sel" : ""}`}
                  style={{
                    left: `${Math.min(100, Math.max(0, (marker.seconds / duration) * 100))}%`,
                  }}
                  onClick={() =>
                    jumpToTimestamp(
                      representative.event.timestamp,
                      representative.index,
                    )
                  }
                  aria-label={`${count} proctoring ${count === 1 ? "event" : "events"} at ${representative.event.timestamp}`}
                  title={
                    count > 1
                      ? `${count} flags near ${representative.event.timestamp}`
                      : `${representative.event.timestamp}: ${representative.event.event_name || representative.event.event_type}`
                  }
                />
              );
            })}
          </div>
          <div className="scrub-legend">
            <span>
              <i />
              Proctoring event · click to jump
            </span>
          </div>
        </div>
      )}
      <div className="recording-link print-recording-link">
        <a className="url" href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
        <a
          className="recording-open no-print"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open recording <OpenInNew />
        </a>
      </div>
    </div>
  );
}

export default function ExecutiveInterviewReport({
  interviewId,
  videoData,
  proctoringData,
  summaryData,
  transcriptData,
  infoItems,
  mainVideoRef,
  secondaryVideoRef,
  activeEventIndex,
  jumpToTimestamp,
  parseTimestampToSeconds,
  formatDateTime,
  onPrint,
}) {
  const metadata = {
    ...videoData,
    ...transcriptData,
    ...summaryData,
    ...proctoringData,
  };
  const candidate =
    metadata.candidate_name ||
    summaryData?.candidate_name ||
    transcriptData?.candidate_name ||
    "Candidate";
  const role = metadata.job_opening_name || metadata.role || "Interview";
  metadata.interview_scheduled_date =
    metadata.interview_scheduled_date || summaryData?.date;
  metadata.interview_scheduled_time =
    metadata.interview_scheduled_time || summaryData?.time;
  const events = Array.isArray(proctoringData?.events)
    ? proctoringData.events
    : [];
  // Candidate metadata can exist even when analysis is unavailable. Never infer a clean session from it.
  const hasAnalysis =
    !!proctoringData &&
    (Array.isArray(proctoringData.events) ||
      numeric(proctoringData.event_count));
  const eventCount = hasAnalysis
    ? Math.max(events.length, Number(proctoringData.event_count) || 0)
    : null;
  const flagged = eventCount > 0;
  const timezone = metadata.interview_timezone;
  const start = metadata.interview_start_datetime;
  const end = metadata.interview_end_datetime;
  const elapsed =
    start && end
      ? (new Date(end).getTime() - new Date(start).getTime()) / 1000
      : NaN;
  const duration =
    Number.isFinite(elapsed) && elapsed >= 0
      ? elapsed
      : numeric(proctoringData?.duration_seconds)
        ? Number(proctoringData.duration_seconds)
        : null;
  const durationLabel =
    duration !== null
      ? `${Math.floor(duration / 60)}m ${Math.floor(duration % 60)}s`
      : "—";
  const time = (value) => {
    if (!value) return "—";
    try {
      return new Date(value).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone: timezone || "UTC",
      });
    } catch {
      return "—";
    }
  };
  const pairs = Array.isArray(transcriptData?.qa_pairs)
    ? transcriptData.qa_pairs
    : [];
  const subs = Array.isArray(summaryData?.sub_scores)
    ? summaryData.sub_scores
    : [];
  const fundamentals =
    subs.find((sub) => /fundamental|concept/i.test(sub.label || "")) || subs[0];
  const coding = subs.find((sub) => /coding/i.test(sub.label || "")) || subs[1];
  const skills = Array.isArray(summaryData?.skills) ? summaryData.skills : [];
  const codingSkills = Array.isArray(summaryData?.coding_skills)
    ? summaryData.coding_skills
    : [];
  const codingPair =
    pairs.find(
      (qa) => qa.code || qa.is_coding || qa.question_type === "coding",
    ) ||
    (codingSkills.length
      ? [...pairs]
          .reverse()
          .find(
            (qa) =>
              !qa.asked_at &&
              codingSkills.some(
                (skill) => skill.tech && skill.tech === qa.tech,
              ),
          ) ||
        [...pairs]
          .reverse()
          .find((qa) =>
            /write.*program|coding|implement|code\b/i.test(qa.question || ""),
          )
      : null);
  const hasCoding = Boolean(codingPair || codingSkills.length);
  const firstConfidence = events.find((event) =>
    numeric(event.confidence),
  )?.confidence;
  const initial = candidate
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  const weighted = skills.filter((skill) =>
    numeric(String(skill.weightage ?? "").replace("%", "")),
  );
  const totalWeight = weighted.reduce(
    (sum, skill) => sum + Number(String(skill.weightage).replace("%", "")),
    0,
  );
  const contribution = (skill) =>
    (percent(skill.score, skill.max || 10) / 100) *
    Number(String(skill.weightage).replace("%", ""));
  const headroom = Math.max(
    0,
    totalWeight - weighted.reduce((sum, skill) => sum + contribution(skill), 0),
  );
  const colors = ["#191F2D", "#4B1498", "#A66FFF", "#C39DFF"];
  const listedNames = [
    ...new Set(
      events
        .map((event) => event.event_name || event.event_type)
        .filter(Boolean),
    ),
  ];
  return (
    <>
      <div
        className={`executive-report ${flagged ? "report-flagged" : "report-clean"}`}
      >
        <header className="topbar no-print">
          <div className="wrap">
            <div className="brand">
              <img src={logo} alt="TalentTalks" />
              <span>AI Interview Platform</span>
            </div>
            <nav className="nav" aria-label="Report sections">
              <a href="#overview">Overview</a>
              <a href="#summary">Summary</a>
              <a href="#integrity">Recording &amp; Proctoring</a>
              <a href="#transcript">Transcript</a>
            </nav>
            <button type="button" className="btn" onClick={onPrint}>
              <Print />
              <span>Print Report</span>
            </button>
          </div>
        </header>
        <div className="hero">
          <div className="hero-network" aria-hidden="true">
            <AnimatedReportNetwork />
          </div>
          <div className="wrap">
            <div>
              <span className={`pill ${flagged ? "warn" : ""}`}>
                <span className="dot" />
                Executive Report ·{" "}
                {flagged
                  ? `${eventCount} proctoring flags`
                  : hasAnalysis
                    ? "Interview Completed"
                    : "Proctoring analysis unavailable"}
              </span>
              <h1>{candidate}</h1>
              <div className="role">
                Interviewed for <b>{role}</b>
              </div>
              <div className="verdict">
                <div className="eyebrow">Assessment Verdict</div>
                <p>
                  {summaryData?.verdict_text ||
                    "The assessment summary is currently unavailable."}
                </p>
              </div>
            </div>
            <div className="gauge-card">
              <div className="gauge">
                <svg
                  width="220"
                  height="220"
                  viewBox="0 0 220 220"
                  role="img"
                  aria-label={`Weightage score: ${scoreText(summaryData?.total_score)} out of 100`}
                >
                  <defs>
                    <linearGradient
                      id="executive-score-gradient"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="1"
                    >
                      <stop offset="0" stopColor="#A66FFF" />
                      <stop offset="1" stopColor="#7CF2B8" />
                    </linearGradient>
                  </defs>
                  <circle
                    cx="110"
                    cy="110"
                    r="94"
                    fill="none"
                    stroke="rgba(255,255,255,.12)"
                    strokeWidth="16"
                  />
                  <circle
                    cx="110"
                    cy="110"
                    r="94"
                    fill="none"
                    stroke="url(#executive-score-gradient)"
                    strokeWidth="16"
                    strokeLinecap="round"
                    strokeDasharray="590.6"
                    strokeDashoffset={
                      590.6 * (1 - percent(summaryData?.total_score) / 100)
                    }
                  />
                </svg>
                <div className="val">
                  <div className="num">
                    {scoreText(summaryData?.total_score)}
                  </div>
                  <div className="lbl">
                    {summaryData?.match_level || "SCORE UNAVAILABLE"}
                  </div>
                </div>
              </div>
              <div className="cap">
                <b>Weightage Score</b>Executive Summary
              </div>
            </div>
          </div>
        </div>
        <main className="wrap">
          <div className="kpis">
            {[
              {
                label: "Fundamentals",
                sub: "Fundamentals & Concepts",
                data: fundamentals,
              },
              ...(hasCoding
                ? [{ label: "Coding", sub: "Coding Proficiency", data: coding }]
                : []),
            ].map((item) => (
              <div className="card kpi" key={item.label}>
                <div className="k">{item.label}</div>
                <div className="v">{item.data?.value ?? "—"}</div>
                <div className="s">{item.sub}</div>
                <div className="bar">
                  <i
                    style={{
                      width: `${percent(parseFloat(item.data?.value))}%`,
                    }}
                  />
                </div>
              </div>
            ))}
            <div className="card kpi">
              <div className="k">Interview Duration</div>
              <div className="v">{durationLabel}</div>
              <div className="s">
                {time(start)} → {time(end)}
              </div>
            </div>
            <div
              className={`card kpi ${flagged ? "alert" : hasAnalysis ? "ok" : ""}`}
            >
              <div className="k">{flagged && <i />}Proctoring Events</div>
              <div className="v">{eventCount ?? "—"}</div>
              <div className="s">
                {flagged
                  ? "Integrity review recommended"
                  : hasAnalysis
                    ? "No proctoring events detected"
                    : "Analysis unavailable"}
              </div>
            </div>
          </div>
          {flagged && (
            <div className="alert-bar">
              <div className="ai">
                <WarningAmberRounded />
              </div>
              <div>
                <b>{eventCount} proctoring events detected in this session</b>
                <span>
                  {listedNames.length
                    ? `${listedNames.join(", ")} ${listedNames.length > 1 ? "were" : "was"} flagged during this session. `
                    : ""}
                  Review the footage before moving this candidate forward.
                </span>
              </div>
              <a href="#integrity">Review events →</a>
            </div>
          )}
          <section id="overview">
            <div className="sec-head">
              <div>
                <div className="eyebrow">Interview Overview</div>
                <h2 className="sec">Session at a glance</h2>
              </div>
            </div>
            <div className="overview">
              <div className="card meta-grid">
                {infoItems.map((item) => (
                  <div className="meta" key={item.label}>
                    <div className="ico">{item.icon}</div>
                    <div>
                      <div className="k">{item.label}</div>
                      <div className="v">
                        {item.value ||
                          (item.label === "Started"
                            ? formatDateTime(start, timezone)
                            : item.label === "Ended"
                              ? formatDateTime(end, timezone)
                              : null) ||
                          metadata[
                            {
                              "Job opening": "job_opening_name",
                              Candidate: "candidate_name",
                              Date: "interview_scheduled_date",
                              Time: "interview_scheduled_time",
                              Timezone: "interview_timezone",
                            }[item.label]
                          ] ||
                          "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="card timeline">
                <div className="eyebrow">Session Timeline</div>
                <div className="tl-track">
                  <div className="tl-fill" />
                  <div className="tl-pt" style={{ left: 0 }} />
                  <div className="tl-pt end" style={{ left: "100%" }} />
                </div>
                <div className="tl-labels">
                  <div>
                    <div className="k">Started</div>
                    <div className="v">{time(start)}</div>
                  </div>
                  <div>
                    <div className="k">Ended</div>
                    <div className="v">{time(end)}</div>
                  </div>
                </div>
                <div className="dur">
                  <div className="big">{durationLabel}</div>
                  <div className="small">
                    Total time on interview ·{" "}
                    {pairs.filter((qa) => qa.answer).length} questions answered
                    <br />
                    {metadata.interview_scheduled_date ||
                      "Date unavailable"} · {timezone || "Timezone unavailable"}
                  </div>
                </div>
                <div className="sched">
                  Scheduled slot:{" "}
                  <b>{metadata.interview_scheduled_time || "—"}</b> on{" "}
                  <b>{metadata.interview_scheduled_date || "—"}</b>
                </div>
              </div>
            </div>
          </section>
          <section id="summary">
            <div className="sec-head">
              <div>
                <div className="eyebrow">Summary Report</div>
                <h2 className="sec">Executive Summary</h2>
              </div>
              <div className="section-actions">
                <p>
                  Skill-level scores with their weightage.
                  {hasCoding
                    ? " Fundamentals and coding are scored separately."
                    : ""}
                </p>
              </div>
            </div>
            {summaryData ? (
              <>
                <div className={`scores ${hasCoding ? "" : "scores-single"}`}>
                  <div className="card panel">
                    <h3>
                      Fundamentals &amp; Concepts{" "}
                      <small>{fundamentals?.value}</small>
                    </h3>
                    {skills.length ? (
                      skills.map((skill, index) => (
                        <Skill key={index} skill={skill} />
                      ))
                    ) : (
                      <p className="report-muted">
                        Skill scores are not available.
                      </p>
                    )}
                    {totalWeight > 0 && (
                      <div className="stack-wrap">
                        <div className="k">
                          How the {fundamentals?.value || "fundamentals score"}{" "}
                          builds up (score × weightage)
                        </div>
                        <div className="stack">
                          {weighted.map((skill, index) => (
                            <span
                              key={index}
                              style={{
                                width: `${(contribution(skill) / totalWeight) * 100}%`,
                                background: colors[index % colors.length],
                              }}
                            >
                              {contribution(skill)
                                .toFixed(2)
                                .replace(/\.00$/, ".0")}
                            </span>
                          ))}
                          {headroom > 0 && (
                            <span
                              className="stack-headroom"
                              style={{
                                width: `${(headroom / totalWeight) * 100}%`,
                              }}
                            >
                              {headroom.toFixed(2).replace(/\.00$/, ".0")}
                            </span>
                          )}
                        </div>
                        <div className="legend">
                          {weighted.map((skill, index) => (
                            <span key={index}>
                              <i
                                style={{
                                  background: colors[index % colors.length],
                                }}
                              />
                              {skill.display_name || skill.tech}
                            </span>
                          ))}
                          {headroom > 0 && (
                            <span>
                              <i className="legend-headroom" />
                              Headroom to {totalWeight}%
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {hasCoding && (
                    <div className="card panel coding">
                      <h3>
                        Coding Proficiency <small>{coding?.value}</small>
                      </h3>
                      {codingSkills.map((skill, index) => (
                        <Skill key={index} skill={skill} />
                      ))}
                      {codingPair ? (
                        <div className="code-hero">
                          <div className="ring" />
                          <div className="big">
                            {codingPair.score ?? "—"}
                            <small>/{codingPair.marks || 10}</small>
                          </div>
                          <div className="coding-challenge">
                            Coding challenge: {codingPair.question}
                          </div>
                          <pre>{codingPair.answer || "No code submitted."}</pre>
                        </div>
                      ) : (
                        <div className="code-hero">
                          <div className="ring" />
                          <div className="big">{coding?.value ?? "—"}</div>
                          <p className="coding-challenge">
                            {codingSkills.length
                              ? "Coding assessment"
                              : "Coding assessment not available"}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {subs
                  .filter((sub) => sub !== fundamentals && sub !== coding)
                  .map((sub, index) => (
                    <div className="additional-score card" key={index}>
                      <span>{sub.label}</span>
                      <b>{sub.value}</b>
                    </div>
                  ))}
              </>
            ) : (
              <EmptyReport
                title="Summary Report Not Available"
                message="The AI summary report could not be generated or loaded."
              />
            )}
          </section>
          <section id="integrity">
            <div className="sec-head">
              <div>
                <div className="eyebrow">Recording &amp; Proctoring</div>
                <h2 className="sec">Evidence and integrity</h2>
              </div>
              {flagged && (
                <p className="section-note">
                  Red markers on the recording timeline show where proctoring
                  flags were raised. Click a marker or an event to jump to that
                  moment.
                </p>
              )}
            </div>
            <div className={flagged ? "integrity2" : "integrity"}>
              <div className="recording-column">
                {videoData?.videoUrl || videoData?.secondCameraUrl ? (
                  <>
                    {videoData.videoUrl && (
                      <Recording
                        url={videoData.videoUrl}
                        videoRef={mainVideoRef}
                        {...{
                          flagged,
                          events,
                          duration,
                          jumpToTimestamp,
                          activeEventIndex,
                          parseTimestampToSeconds,
                        }}
                      />
                    )}
                    {videoData.secondCameraUrl && (
                      <Recording
                        url={videoData.secondCameraUrl}
                        secondary
                        videoRef={secondaryVideoRef}
                        {...{
                          flagged,
                          events,
                          duration,
                          jumpToTimestamp,
                          activeEventIndex,
                          parseTimestampToSeconds,
                        }}
                      />
                    )}
                  </>
                ) : (
                  <EmptyReport
                    title="Recording Not Available"
                    message="The interview recordings could not be loaded or have not been uploaded yet."
                  />
                )}
              </div>
              {flagged ? (
                <div className="card proc2">
                  <div className="proc2-head">
                    <div>
                      <div className="eyebrow">Proctoring Events</div>
                    </div>
                    <span className="cnt">{eventCount}</span>
                  </div>
                  <div className="proc-stats">
                    <div>
                      <div className="k">Total flags</div>
                      <div className="v r">{eventCount}</div>
                    </div>
                    <div>
                      <div className="k">First flag</div>
                      <div className="v r">{events[0]?.timestamp || "—"}</div>
                    </div>
                    <div>
                      <div className="k">Confidence</div>
                      <div className="v">
                        {numeric(firstConfidence)
                          ? `${Math.round(Number(firstConfidence) * 100)}%`
                          : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="risk">
                    <div className="row">
                      <span>Integrity risk</span>
                      <b>High · review required</b>
                    </div>
                    <div className="grad">
                      <i />
                    </div>
                    <div className="ticks">
                      <span>Clean</span>
                      <span>Moderate</span>
                      <span>High</span>
                    </div>
                  </div>
                  <div className="ev-list">
                    {events.length ? (
                      events.map((event, index) => (
                        <button
                          type="button"
                          className={`ev ${activeEventIndex === index ? "sel" : ""}`}
                          key={index}
                          onClick={() =>
                            jumpToTimestamp(event.timestamp, index)
                          }
                          aria-pressed={activeEventIndex === index}
                        >
                          <span className="tri">
                            <WarningAmberRounded />
                          </span>
                          <span className="event-content">
                            <b>
                              {event.event_name ||
                                event.event_type ||
                                "Proctoring event"}
                            </b>
                            <span className="meta2">
                              <code>{event.timestamp || "—"}</code>
                              {numeric(event.confidence) &&
                                `${Math.round(Number(event.confidence) * 100)}% confidence`}
                            </span>
                            {event.reason && (
                              <span className="desc">{event.reason}</span>
                            )}
                          </span>
                          <span className="go no-print">Jump →</span>
                        </button>
                      ))
                    ) : (
                      <p className="report-muted">
                        Event details are currently unavailable.
                      </p>
                    )}
                  </div>
                  <div className="ev-foot">
                    <span>
                      Showing {events.length} of {eventCount} events
                    </span>
                    <span className="no-print">Click an event to review</span>
                  </div>
                </div>
              ) : hasAnalysis ? (
                <div className="card proc">
                  <span className="count">0</span>
                  <div className="eyebrow">Proctoring Events</div>
                  <div className="shield">
                    <ShieldOutlined />
                    <CheckRounded className="shield-check" />
                  </div>
                  <h3>Clean session</h3>
                  <p>No proctoring events detected</p>
                </div>
              ) : (
                <EmptyReport
                  title="No Proctoring Report Available"
                  message="Proctoring analysis was not performed or could not be loaded."
                />
              )}
            </div>
          </section>
          <section id="transcript">
            <div className="sec-head">
              <div>
                <div className="eyebrow">Transcript Report</div>
                <h2 className="sec">Question-by-question</h2>
              </div>
              <div className="section-actions">
                <p>
                  Every exchange, verbatim, with timestamps, response time and
                  score. Jump to any question below.
                </p>
              </div>
            </div>
            {pairs.length ? (
              <>
                <nav
                  className="card qnav no-print"
                  aria-label="Interview questions"
                >
                  {pairs.map((qa, index) => (
                    <a
                      className={scoreClass(qa)}
                      key={index}
                      href={`#question-${index + 1}`}
                    >
                      <span className="q">Q{index + 1}</span>
                      <span className="sc">{qa.score ?? "—"}</span>
                    </a>
                  ))}
                </nav>
                <div className="card chat">
                  {pairs.map((qa, index) => {
                    const responseSeconds =
                      qa.asked_at && qa.answered_at
                        ? (new Date(qa.answered_at) - new Date(qa.asked_at)) /
                          1000
                        : NaN;
                    return (
                      <article
                        className="turn"
                        id={`question-${index + 1}`}
                        key={index}
                      >
                        <div className="turn-head">
                          <span className="qnum">Q{index + 1}</span>
                          <span className="line" />
                          {Number.isFinite(responseSeconds) &&
                            responseSeconds >= 0 && (
                              <span className="rt">
                                <AccessTimeRounded />
                                {Math.round(responseSeconds)}s response
                              </span>
                            )}
                        </div>
                        <div className="msg int">
                          <div className="avatar ai">
                            <img
                              src={reportAvatar}
                              alt="TalentTalks interviewer"
                            />
                          </div>
                          <div className="bubble">
                            <div className="who">
                              Interviewer
                              {qa.asked_at && (
                                <time
                                  dateTime={qa.asked_at}
                                  title={formatDateTime(qa.asked_at, timezone)}
                                >
                                  {formatDateTime(qa.asked_at, timezone)}
                                </time>
                              )}
                            </div>
                            <div className="text">{qa.question}</div>
                          </div>
                        </div>
                        {qa.answer && (
                          <div className="msg cand">
                            <div className="avatar c">{initial}</div>
                            <div className="bubble">
                              <div className="who">
                                Candidate
                                {qa.answered_at && (
                                  <time
                                    dateTime={qa.answered_at}
                                    title={formatDateTime(
                                      qa.answered_at,
                                      timezone,
                                    )}
                                  >
                                    {formatDateTime(qa.answered_at, timezone)}
                                  </time>
                                )}
                              </div>
                              <div
                                className={`text ${qa === codingPair || qa.code || qa.is_coding ? "code" : ""}`}
                              >
                                {qa.answer}
                              </div>
                              {numeric(qa.score) && (
                                <span
                                  className={`score-chip ${scoreClass(qa)}`}
                                >
                                  Score: {qa.score}
                                  {qa.marks ? `/${qa.marks}` : ""}
                                </span>
                              )}
                              <div className="clear" />
                            </div>
                          </div>
                        )}
                        {!qa.answer && numeric(qa.score) && (
                          <span className={`score-chip ${scoreClass(qa)}`}>
                            Score: {qa.score}
                            {qa.marks ? `/${qa.marks}` : ""}
                          </span>
                        )}
                      </article>
                    );
                  })}
                </div>
              </>
            ) : (
              <EmptyReport
                title="Transcript Not Available"
                message="The interview transcript is currently unavailable."
              />
            )}
          </section>
        </main>
        <div className="report-print-footer">
          <img src={logo} alt="TalentTalks" />
          <span>Executive interview report · {interviewId}</span>
        </div>
      </div>
      <div className="executive-report-footer no-print">
        <Footer />
      </div>
    </>
  );
}
